import { useEffect, useRef, useState } from "react";

import {
  BobbinMobileError,
  type BobbinMobileClient,
} from "./client";
import {
  bobbinAttentionReason,
  bobbinSessionContext,
  bobbinSessionLabel,
  formatBobbinTime,
  redactBobbinSecrets,
} from "./presentation";
import type {
  BobbinActivityItem,
  BobbinAgentSession,
  BobbinAttentionItem,
} from "./types";

export type BobbinOperationalKind = "activity" | "sessions" | "attention";

interface BobbinOperationalViewProps {
  client: BobbinMobileClient;
  csrfToken: string;
  kind: BobbinOperationalKind;
}

type OperationalData =
  | BobbinActivityItem[]
  | BobbinAgentSession[]
  | BobbinAttentionItem[];

const TITLES: Record<BobbinOperationalKind, { eyebrow: string; title: string }> = {
  activity: { eyebrow: "Recent", title: "Activity" },
  sessions: { eyebrow: "Agents", title: "Sessions" },
  attention: { eyebrow: "Inbox", title: "Attention" },
};

function safeMessage(error: unknown): string {
  if (error instanceof BobbinMobileError) return error.message;
  if (error instanceof DOMException && error.name === "AbortError") return "";
  return "Bobbin could not complete that request.";
}

export function BobbinOperationalView({
  client,
  csrfToken,
  kind,
}: BobbinOperationalViewProps) {
  const [data, setData] = useState<OperationalData>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const requestRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    requestRef.current = controller;
    setLoading(true);
    setError("");
    const request = kind === "activity"
      ? client.callTool("list_activity", { limit: 100 }, csrfToken, controller.signal)
      : kind === "sessions"
        ? client.callTool(
            "list_sessions",
            { active_only: false, limit: 100 },
            csrfToken,
            controller.signal,
          )
        : client.callTool(
            "list_attention",
            { min_importance: 0, limit: 100 },
            csrfToken,
            controller.signal,
          );
    request
      .then((result) => setData(result))
      .catch((requestError) => setError(safeMessage(requestError)))
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [client, csrfToken, kind]);

  const title = TITLES[kind];

  return (
    <section className="bobbin-mobile__view" aria-labelledby={`bobbin-${kind}-title`}>
      <header className="bobbin-mobile__view-header">
        <div>
          <span className="bobbin-mobile__eyebrow">{title.eyebrow}</span>
          <h1 id={`bobbin-${kind}-title`}>{title.title}</h1>
        </div>
      </header>
      {error && <div className="bobbin-mobile__alert" role="alert">{error}</div>}
      {loading ? (
        <div className="bobbin-mobile__empty">Loading {title.title.toLowerCase()}…</div>
      ) : data.length ? (
        <div className="bobbin-mobile__card-list">
          {kind === "activity" && (data as BobbinActivityItem[]).map((item) => (
            <article className="bobbin-mobile__info-card" key={item.id}>
              <header>
                <strong>{redactBobbinSecrets(item.actor || "Agent")}</strong>
                <span>{formatBobbinTime(item.ts)}</span>
              </header>
              <p>{redactBobbinSecrets(item.verb || "worked on")} <strong>{redactBobbinSecrets(item.object || "Bobbin")}</strong></p>
              {item.branch && (
                <span className="bobbin-mobile__pill">{redactBobbinSecrets(item.branch)}</span>
              )}
            </article>
          ))}
          {kind === "sessions" && (data as BobbinAgentSession[]).map((item) => (
            <article className="bobbin-mobile__info-card" key={item.id}>
              <header>
                <strong>{bobbinSessionLabel(item)}</strong>
                <span>{String(item.status || "unknown")}</span>
              </header>
              <p>{bobbinSessionContext(item)}</p>
              <span className="bobbin-mobile__muted">
                {formatBobbinTime(item.last_heartbeat || item.started_at)}
              </span>
            </article>
          ))}
          {kind === "attention" && (data as BobbinAttentionItem[]).map((item) => (
            <article className="bobbin-mobile__info-card bobbin-mobile__info-card--attention" key={item.id}>
              <header>
                <strong>{bobbinAttentionReason(item)}</strong>
                <span>Importance {String(item.metadata?.importance ?? 0)}</span>
              </header>
              <p>{item.source_node_id ? `From ${item.source_node_id.slice(0, 8)}` : "Source unavailable"}</p>
            </article>
          ))}
        </div>
      ) : (
        <div className="bobbin-mobile__empty">Nothing here right now.</div>
      )}
    </section>
  );
}
